import { MutableRefObject, useEffect } from "react";
import { ICustomScrollStore } from "../../_store/CustomScrollStoreModel";
import {
    IPerfectScrollExtensions,
    IPerfectScrollState,
} from "../../scroll/perfectScrollModel";
import { useCustomScrollObjectSelector } from "../../_store/customScrollSelectorHook";
import { noopFn } from "../../_utils/fnUtils";
import { getScrollContainerProps } from "../../_shared/scrollOffsetDomain";
import { setDimensionsAction } from "../setOffsetsOnScroll/setOffsetsOnScrollActions";
import { isObjectPropsEquals } from "../../_utils/equality";

export function useRecalculateDimensionsOnAnimationFrameFeature(
    containerRef: MutableRefObject<HTMLElement>,
    store: ICustomScrollStore,
    extensions: IPerfectScrollExtensions
) {
    const {
        suppressScrollX,
        suppressScrollY,
        useAnimationFrame,
    } = useCustomScrollObjectSelector(selector, { store });

    useEffect(() => {
        const element = containerRef.current;
        if (!element || !useAnimationFrame) return noopFn;

        return observeRect(
            containerRef.current,
            node =>
                getScrollContainerProps(
                    node,
                    extensions?.getScrollContainerProps
                ),
            rect => {
                store.dispatch(setDimensionsAction(rect));
            }
        );
    }, [
        suppressScrollX,
        suppressScrollY,
        useAnimationFrame,
        containerRef.current,
    ]);
}

const selector = (s: IPerfectScrollState) => ({
    suppressScrollX: s?.options?.suppressScrollX,
    suppressScrollY: s?.options?.suppressScrollY,
    useAnimationFrame: s?.options?.useAnimationFrame,
});

const observedNodes = new Map<Element, any>();
let rafId: number;

const run = getRec => {
    const changedStates = [];
    observedNodes.forEach((state, node) => {
        const newRect = getRec(node);
        if (!isObjectPropsEquals(newRect, state.rect)) {
            state.rect = newRect;
            changedStates.push(state);
        }
    });

    changedStates.forEach(state => {
        state.callbacks.forEach(cb => cb(state.rect));
    });

    rafId = window.requestAnimationFrame(() => run(getRec));
};

function observeRect(node: Element, getRec, cb) {
    let wasEmpty = observedNodes.size === 0;
    if (observedNodes.has(node)) {
        observedNodes.get(node)!.callbacks.push(cb);
    } else {
        observedNodes.set(node, {
            rect: undefined,
            hasRectChanged: false,
            callbacks: [cb],
        });
    }
    if (wasEmpty) run(getRec);

    return () => {
        let state = observedNodes.get(node);
        if (state) {
            // Remove the callback
            const index = state.callbacks.indexOf(cb);
            if (index >= 0) state.callbacks.splice(index, 1);

            // Remove the node reference
            if (!state.callbacks.length) observedNodes.delete(node);

            // Stop the loop
            if (!observedNodes.size) cancelAnimationFrame(rafId);
        }
    };
}
